import React from "react";
import { API_BASE_URL } from "../../../constants";
import { guardSession } from "../../auth/v2/sessionBinding";

// Kobi, 11/04: the social score shows as soon as it is calculated. The
// first read of an athlete's accounts runs in the background and takes
// minutes, not seconds (production: about 3 minutes with Instagram alone,
// 10 to 20 with TikTok and X as well). While that run is on its way the
// onboarding state says social.pending (with pendingPlatforms), until the
// CacheScore and NIL numbers that use the new score are written too.
//
// watchSocialScore reads that state every SOCIAL_POLL_MS (skipping a tick
// while the tab is hidden) until the run is done, then calls onLanded with
// the fresh state; it gives up after SOCIAL_WATCH_MAX_MS or a few failed
// reads in a row. onPending(true) once a run is seen on its way,
// onPending(false) when the watch ends either way. `first` is a state
// already read (no read is made for it); `read` is how the state is read
// (the wizard and the edit hub pass their own getOnboarding). Returns a
// stop function.
export const SOCIAL_POLL_MS = 15000;
export const SOCIAL_WATCH_MAX_MS = 30 * 60 * 1000;
const MAX_FAILED_READS = 3;
const READ_TIMEOUT_MS = 20000;

export const socialPending = (s) => !!s?.social?.pending;

// GET athlete/onboarding/ with the signed-in token and a time limit, for
// pages outside the wizard (no axios: the profile pages stay light).
export const readOnboardingState = async ({ timeoutMs = READ_TIMEOUT_MS } = {}) => {
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const res = await fetch(`${API_BASE_URL}/athlete/onboarding/`, {
      headers: { Authorization: `Bearer ${guardSession()}` },
      ...(ctrl ? { signal: ctrl.signal } : {}),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const watchSocialScore = ({
  onLanded,
  onPending = () => {},
  alive = () => true,
  first = null,
  read = readOnboardingState,
  intervalMs = SOCIAL_POLL_MS,
  maxMs = SOCIAL_WATCH_MAX_MS,
} = {}) => {
  const started = Date.now();
  let stopped = false;
  let timer = null;
  let failed = 0;
  let before;
  let seen = false;

  const stop = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
  const finish = (state) => {
    stop();
    if (seen) onPending(false);
    if (state && onLanded) onLanded(state);
  };
  const schedule = () => {
    if (!stopped) timer = setTimeout(tick, intervalMs);
  };
  const check = (s) => {
    if (stopped) return;
    const at = s?.social?.computedAt || null;
    if (before === undefined) {
      before = at;
      if (!socialPending(s)) { finish(null); return; }
      seen = true;
      onPending(true);
      schedule();
      return;
    }
    if (!socialPending(s)) { finish(s); return; }
    // A run wrote a score while another is still reading: show it, keep on.
    if (at !== before && onLanded) { before = at; onLanded(s); }
    schedule();
  };
  async function tick() {
    if (stopped) return;
    if (!alive()) { stop(); return; }
    if (Date.now() - started >= maxMs) { finish(null); return; }
    if (typeof document !== "undefined" && document.hidden) { schedule(); return; }
    let s;
    try {
      s = await read({ timeoutMs: READ_TIMEOUT_MS });
    } catch {
      failed += 1;
      if (failed >= MAX_FAILED_READS) finish(null);
      else schedule();
      return;
    }
    if (stopped || !alive()) { stop(); return; }
    failed = 0;
    check(s);
  }

  if (first) check(first);
  else {
    // The first read: is a run on its way at all?
    (async () => {
      try {
        const s = await read({ timeoutMs: READ_TIMEOUT_MS });
        if (alive()) check(s);
        else stop();
      } catch {
        finish(null);
      }
    })();
  }
  return stop;
};

// The note shown while the first read runs (profile pages).
export const SOCIAL_CALCULATING_TEXT = "Calculating your social score. This usually takes a few minutes, and it shows here as soon as it is ready.";

export const SocialScoreCalculating = ({ className = "" }) => (
  <div role="status" className={`flex items-center gap-3 rounded-[12px] bg-bm-subtle px-4 py-3 ${className}`}>
    <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-bm-mutedbg border-t-bm-orange" aria-hidden="true" />
    <p className="bm-body-s text-bm-ink">{SOCIAL_CALCULATING_TEXT}</p>
  </div>
);
